import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import api from '../../utils/api';
import {
    BadgeIndianRupee,
    BarChart3,
    CalendarDays,
    ChevronDown,
    Download,
    Eye,
    FileSpreadsheet,
    FileText,
    Filter,
    Info,
    LayoutDashboard,
    Loader2,
    RotateCcw,
    Save,
    Search,
    ShieldCheck,
    Users
} from 'lucide-react';
import Sidebar from '../../components/Sidebar/sidebar';
import Header from '../../components/Header';
import { getUserPrivileges, getUserRole } from '../../utils/auth';
import './irdaiReports.css';
import { API_BASE_URL } from '../../utils/apiBase';

const API_BASE = `${API_BASE_URL}/api/irdai-reports`;
const PRESET_KEY = 'irdaiReportFilterPresets';

const generateFinancialYears = (pastCount = 5, futureCount = 5) => {
    const list = [];
    const now = new Date();
    const currentFYStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;

    for (let i = pastCount; i >= 1; i--) {
        const a = currentFYStartYear - i;
        list.push(`${a}-${a + 1}`);
    }

    for (let i = 0; i <= futureCount; i++) {
        const a = currentFYStartYear + i;
        list.push(`${a}-${a + 1}`);
    }
    return list;
};

const quarters = [
    { value: 'Q1', label: 'Q1 (Apr-May-Jun)' },
    { value: 'Q2', label: 'Q2 (Jul-Aug-Sep)' },
    { value: 'Q3', label: 'Q3 (Oct-Nov-Dec)' },
    { value: 'Q4', label: 'Q4 (Jan-Feb-Mar)' },
    { value: 'ALL', label: 'Full Financial Year' }
];

const reportCards = [
    {
        value: 'form10',
        title: 'Form 10',
        eyebrow: 'Composite return',
        description: 'Six section view covering insurer-wise and client-wise Life, Non-Life, and rider business.',
        accent: 'blue',
        icon: LayoutDashboard
    },
    {
        value: 'form10.1',
        title: 'Form 10.1',
        eyebrow: 'Life business',
        description: 'Life matrix by linked, non-linked, riders, individual and group premium values.',
        accent: 'green',
        icon: ShieldCheck
    },
    {
        value: 'form10.2',
        title: 'Form 10.2',
        eyebrow: 'General business',
        description: 'Line of business totals for Engineering, Fire, Health, Marine, Miscellaneous, and Motor.',
        accent: 'amber',
        icon: BarChart3
    }
];

const visibleColsByForm = {
    'form10.1': [
        'Heading',
        'Line of Business',
        'Individual Business - No. of Policies',
        'Individual Business - Premium Amount',
        'Individual Business - Brokerage Income',
        'Group Business - No. of Lives Covered',
        'Group Business - Premium Amount',
        'Group Business - Brokerage Income'
    ],
    'form10.2': [
        'Line of Business',
        'No. of Policies',
        'Premium amount',
        'Brokerage Income'
    ]
};

const form10Sections = [
    {
        key: 'section1',
        label: 'Non-Life Insurers',
        description: 'Name of Non Life Insurers',
        nameKey: 'Name of Non Life Insurers',
        cols: [
            'Name of Non Life Insurers',
            'Engineering-No. of Policies',
            'Engineering-Premium',
            'Fire-No. of Policies',
            'Fire-Premium',
            'Health-No. of Policies',
            'Health-Premium',
            'Liability-No. of Policies',
            'Liability-Premium',
            'Marine Cargo-No. of Policies',
            'Marine Cargo-Premium',
            'Marine Hull-No. of Policies',
            'Marine Hull-Premium',
            'Misc-No. of Policies',
            'Misc-Premium',
            'Motor-No. of Policies',
            'Motor-Premium'
        ]
    },
    {
        key: 'section2',
        label: 'Life Insurers',
        description: 'Name of Life Insurers',
        nameKey: 'Name of Life Insurers',
        cols: [
            'Name of Life Insurers',
            'Life-No. of Policies',
            'Life-Premium',
            'Health-No. of Policies',
            'Health-Premium',
            'Linked-No. of Policies',
            'Linked-Premium',
            'Annuity-No. of Policies',
            'Annuity-Premium',
            'Life-No. of Policies 2',
            'Life-Premium 2',
            'Health-No. of Policies 2',
            'Health-Premium 2',
            'Pension-No. of Policies',
            'Pension-Premium',
            'Annuity-No. of Policies 2',
            'Annuity-Premium 2'
        ]
    },
    {
        key: 'section3',
        label: 'Life Riders',
        description: 'Name of Life Riders Insurers',
        nameKey: 'Name of Life Riders Insurers',
        cols: [
            'Name of Life Riders Insurers',
            'Health+sickness-No. of Policies',
            'Health+Sickness-Premium',
            'Accident+disability-No. of Policies',
            'Accident+disability-Premium',
            'Linked-No. of Policies',
            'Linked-Premium',
            'Others-No. of Policies',
            'Others-Premium',
            'Health+sickness-No. of Policies 2',
            'Health+sickness-Premium 2',
            'Accident+disability-No. of Policies 2',
            'Accident+disability-Premium 2',
            'Term-No. of Policies',
            'Term-Premium',
            'Others-No.of Policies 2',
            'Others-Premium 2'
        ]
    },
    {
        key: 'section4',
        label: 'Non-Life Clients',
        description: 'Client Name - Non Life (General)',
        nameKey: 'Client Name',
        cols: [
            'Client Name',
            'Engineering-No. of Policies',
            'Engineering-Premium',
            'Fire-No. of Policies',
            'Fire-Premium',
            'Health-No. of Policies',
            'Health-Premium',
            'Liability-No. of Policies',
            'Liability-Premium',
            'Marine Cargo-No. of Policies',
            'Marine Cargo-Premium',
            'Marine Hull-No. of Policies',
            'Marine Hull-Premium',
            'Misc-No. of Policies',
            'Misc-Premium',
            'Motor-No. of Policies',
            'Motor-Premium'
        ]
    },
    {
        key: 'section5',
        label: 'Life Clients',
        description: 'Client Name Life',
        nameKey: 'Client Name Life',
        cols: [
            'Client Name Life',
            'Life-No. of Policies',
            'Life-Premium',
            'Health-No. of Policies',
            'Health-Premium',
            'Linked-No. of Policies',
            'Linked-Premium',
            'Annuity-No. of Policies',
            'Annuity-Premium',
            'Life-No. of Policies 2',
            'Life-Premium 2',
            'Non-Linked-No. of Policies',
            'Non-Linked-Premium',
            'Pension-No. of Policies',
            'Pension-Premium',
            'Annuity-No. of Policies 2',
            'Annuity-Premium 2'
        ]
    },
    {
        key: 'section6',
        label: 'Rider Clients',
        description: 'Client Name Riders',
        nameKey: 'Client Name Riders',
        cols: [
            'Client Name Riders',
            'Health+sickness-No. of Policies',
            'Health+sickness-Premium',
            'Accident+disability-No. of Policies',
            'Accident+disability-Premium',
            'Term-No. of Policies',
            'Term-Premium',
            'Others-No. of Policies',
            'Others-Premium',
            'Health+sickness-No. of Policies 2',
            'Health+sickness-Premium 2',
            'Accident+disability-No. of Policies 2',
            'Accident+disability-Premium 2',
            'Term-No. of Policies 2',
            'Term-Premium 2',
            'Others-No. of Policies 2',
            'Others-Premium 2'
        ]
    }
];

const emptyFilters = {
    insurer: '',
    client: '',
    coverageType: ''
};

const formatAmount = value => {
    const number = Number(value || 0);
    return number.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const getRowsTotal = (rows, matcher) => rows.reduce((sum, row) => {
    return sum + Object.entries(row).reduce((innerSum, [key, value]) => {
        if (!matcher(key)) return innerSum;
        const number = Number(value);
        return innerSum + (Number.isNaN(number) ? 0 : number);
    }, 0);
}, 0);

const getStoredList = key => {
    try {
        const parsed = JSON.parse(localStorage.getItem(key) || '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const IrdaiReports = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [financialYears] = useState(generateFinancialYears(5, 5));
    const [selectedFY, setSelectedFY] = useState('');
    const [selectedQuarter, setSelectedQuarter] = useState('ALL');
    const [selectedForm, setSelectedForm] = useState('');
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(emptyFilters);
    const [section4ClientMode, setSection4ClientMode] = useState('top10');
    const [activeSection, setActiveSection] = useState('section1');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
    const [savedPresets, setSavedPresets] = useState(() => getStoredList(PRESET_KEY));
    const [exportHistory, setExportHistory] = useState([]);
    const [historySearch, setHistorySearch] = useState('');
    const [historyPage, setHistoryPage] = useState(1);
    const [historyPagination, setHistoryPagination] = useState({ page: 1, limit: 5, total: 0, totalPages: 1 });

    const [form10Data, setForm10Data] = useState({
        section1: [],
        section2: [],
        section3: [],
        section4: [],
        section5: [],
        section6: []
    });

    const role = getUserRole();
    const privileges = getUserPrivileges();
    const canExport = role === 'admin' || privileges.length === 0 || privileges.some(privilege => {
        const key = typeof privilege === 'string' ? privilege : privilege?.key;
        return String(key || '').toLowerCase().includes('report');
    });

    useEffect(() => {
        setReportData([]);
        setForm10Data({
            section1: [],
            section2: [],
            section3: [],
            section4: [],
            section5: [],
            section6: []
        });
        setSearchTerm('');
        setSortConfig({ key: '', direction: 'asc' });
        setActiveSection('section1');

    }, [selectedForm]);

    useEffect(() => {
        fetchExportHistory(historyPage, historySearch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [historyPage]);

    const updateFilter = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const getRequestParams = (clientMode = section4ClientMode) => {
        const params = {};
        if (selectedFY) params.financialYear = selectedFY;
        if (selectedQuarter && selectedQuarter !== 'ALL') params.quarter = selectedQuarter;
        if (selectedForm === 'form10') params.clientMode = clientMode;
        return params;
    };

    const fetchForm101 = async (templateOnly = false) => {
        setLoading(true);
        try {
            const url = templateOnly || !selectedFY ? `${API_BASE}/form101/template` : `${API_BASE}/form101`;
            const res = await axios.get(url, { params: getRequestParams() });
            setReportData(res.data.data || []);
        } catch (err) {
            console.error('Error fetching Form 10.1', err);
            alert('Failed to fetch Form 10.1 report');
        } finally {
            setLoading(false);
        }
    };

    const fetchForm102 = async (templateOnly = false) => {
        setLoading(true);
        try {
            const url = templateOnly || !selectedFY ? `${API_BASE}/form102/template` : `${API_BASE}/form102`;
            const res = await axios.get(url, { params: getRequestParams() });
            setReportData(res.data.data || []);
        } catch (err) {
            console.error('Error fetching Form 10.2', err);
            alert('Failed to fetch Form 10.2 report');
        } finally {
            setLoading(false);
        }
    };

    const fetchForm10 = async (clientMode = section4ClientMode) => {
        if (!selectedFY) {
            alert('Please select Financial Year for Form 10.');
            return;
        }

        setLoading(true);
        try {
            const params = getRequestParams(clientMode);
            const [s1, s2, s3, s4, s5, s6] = await Promise.all([
                axios.get(`${API_BASE}/form10/section1`, { params }),
                axios.get(`${API_BASE}/form10/section2`, { params }),
                axios.get(`${API_BASE}/form10/section3`, { params }),
                axios.get(`${API_BASE}/form10/section4`, { params }),
                axios.get(`${API_BASE}/form10/section5`, { params }),
                axios.get(`${API_BASE}/form10/section6`, { params })
            ]);

            setForm10Data({
                section1: s1.data.data || [],
                section2: s2.data.data || [],
                section3: s3.data.data || [],
                section4: s4.data.data || [],
                section5: s5.data.data || [],
                section6: s6.data.data || []
            });
        } catch (err) {
            console.error('Error fetching Form 10 sections', err);
            alert('Failed to fetch Form 10 report');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        if (!selectedForm) {
            alert('Please select a Form.');
            return;
        }

        if (selectedForm === 'form10') fetchForm10();
        if (selectedForm === 'form10.1') fetchForm101(false);
        if (selectedForm === 'form10.2') fetchForm102(false);
    };

    const generateReport = formValue => {
        if (!formValue) return;
        setSelectedForm(formValue);

        if (formValue === 'form10') fetchForm10();
        if (formValue === 'form10.1') fetchForm101(false);
        if (formValue === 'form10.2') fetchForm102(false);
    };

    const clearFilters = () => {
        setSelectedFY('');
        setSelectedQuarter('ALL');
        setSelectedForm('');
        setFilters(emptyFilters);
        setReportData([]);
        setForm10Data({
            section1: [],
            section2: [],
            section3: [],
            section4: [],
            section5: [],
            section6: []
        });
        setSearchTerm('');
        setSection4ClientMode('top10');
        setActiveSection('section1');
    };

    const savePreset = () => {
        const preset = {
            id: Date.now(),
            name: `${selectedForm || 'Report'} - ${selectedFY || 'All FY'} - ${selectedQuarter}`,
            selectedForm,
            selectedFY,
            selectedQuarter,
            filters,
            section4ClientMode
        };
        const next = [preset, ...savedPresets].slice(0, 5);
        setSavedPresets(next);
        localStorage.setItem(PRESET_KEY, JSON.stringify(next));
    };

    const applyPreset = preset => {
        setSelectedForm(preset.selectedForm || '');
        setSelectedFY(preset.selectedFY || '');
        setSelectedQuarter(preset.selectedQuarter || 'ALL');
        setFilters(preset.filters || emptyFilters);
        setSection4ClientMode(preset.section4ClientMode || 'top10');
    };

    const fetchExportHistory = async (page = historyPage, q = historySearch) => {
        try {
            const res = await api.get(`${API_BASE}/export-history`, {
                params: { page, limit: 5, q }
            });
            setExportHistory(res.data.data || []);
            setHistoryPagination(res.data.pagination || { page, limit: 5, total: 0, totalPages: 1 });
        } catch (err) {
            console.error('Error loading export history', err);
            setExportHistory([]);
        }
    };

    const rememberExport = async (filename, reportType) => {
        const card = reportCards.find(report => report.value === selectedForm);
        try {
            await api.post(`${API_BASE}/export-history`, {
                reportName: card?.title || selectedForm || 'IRDAI Report',
                formName: selectedForm || 'IRDAI',
                reportType,
                fileName: filename
            });
            setHistoryPage(1);
            fetchExportHistory(1, historySearch);
        } catch (err) {
            console.error('Error saving export history', err);
        }
    };

    const exportReport = async (format = 'xlsx') => {
        if (!selectedForm) {
            alert('Please select a Form.');
            return;
        }

        try {
            const endpoint = selectedForm === 'form10.1'
                ? 'form101/download'
                : selectedForm === 'form10.2'
                    ? 'form102/download'
                    : 'form10/download';

            const params = { ...getRequestParams(), format };
            const response = await axios.get(`${API_BASE}/${endpoint}`, { params, responseType: 'blob' });
            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const link = document.createElement('a');
            const filename = `IRDAI_${selectedForm}_${selectedFY || 'ALL'}_${selectedQuarter || 'ALL'}.${format}`;
            const objectUrl = window.URL.createObjectURL(blob);
            link.href = objectUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(objectUrl);
            await rememberExport(filename, format === 'csv' ? 'CSV' : 'Excel');
        } catch (err) {
            console.error('Export error', err);
            alert(`Failed to export ${format === 'csv' ? 'CSV' : 'Excel'}`);
        }
    };

    const activeFormCard = reportCards.find(card => card.value === selectedForm);
    const activeSectionConfig = form10Sections.find(section => section.key === activeSection) || form10Sections[0];
    const form10Rows = activeSectionConfig ? form10Data[activeSectionConfig.key] || [] : [];
    const visibleCols = selectedForm === 'form10'
        ? activeSectionConfig.cols
        : visibleColsByForm[selectedForm] || Object.keys(reportData[0] || {});
    const rawRows = selectedForm === 'form10' ? form10Rows : reportData;

    const tableRows = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        const insurerFilter = filters.insurer.trim().toLowerCase();
        const clientFilter = filters.client.trim().toLowerCase();
        const coverageTypeFilter = filters.coverageType.trim().toLowerCase();

        const filtered = rawRows.filter(row => {
            const rowText = Object.values(row).join(' ').toLowerCase();
            if (term && !rowText.includes(term)) return false;
            if (insurerFilter && !String(row['Name of Non Life Insurers'] || row['Name of Life Insurers'] || row['Name of Life Riders Insurers'] || '').toLowerCase().includes(insurerFilter)) return false;
            if (clientFilter && !String(row['Client Name'] || row['Client Name Life'] || row['Client Name Riders'] || '').toLowerCase().includes(clientFilter)) return false;
            if (coverageTypeFilter && !rowText.includes(coverageTypeFilter)) return false;
            return true;
        });

        if (!sortConfig.key) return filtered;

        return [...filtered].sort((a, b) => {
            const aValue = a[sortConfig.key] ?? '';
            const bValue = b[sortConfig.key] ?? '';
            const aNumber = Number(aValue);
            const bNumber = Number(bValue);
            const direction = sortConfig.direction === 'asc' ? 1 : -1;

            if (!Number.isNaN(aNumber) && !Number.isNaN(bNumber)) {
                return (aNumber - bNumber) * direction;
            }

            return String(aValue).localeCompare(String(bValue)) * direction;
        });
    }, [rawRows, searchTerm, filters, sortConfig]);

    const summary = useMemo(() => {
        const rows = rawRows.filter(row => {
            const firstValue = Object.values(row)[0];
            return String(firstValue || '').toUpperCase() !== 'NIL';
        });
        return {
            rows: rows.length,
            policies: getRowsTotal(rows, key => key.toLowerCase().includes('no.') && key.toLowerCase().includes('polic')),
            premium: getRowsTotal(rows, key => key.toLowerCase().includes('premium')),
            brokerage: getRowsTotal(rows, key => key.toLowerCase().includes('brokerage'))
        };
    }, [rawRows]);

    const hasReportData = selectedForm === 'form10'
        ? Object.values(form10Data).some(rows => rows.length > 0)
        : reportData.length > 0;

    const handleSort = key => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const renderSkeleton = () => (
        <div className="irdai-skeleton-table">
            {Array.from({ length: 6 }).map((_, idx) => (
                <span key={idx} />
            ))}
        </div>
    );

    const renderTable = () => {
        if (loading) return renderSkeleton();

        if (!selectedForm) {
            return (
                <div className="irdai-empty-state">
                    <FileText size={34} />
                    <h3>Select a report to begin</h3>
                    <p>Choose a report card, configure filters, then generate a preview.</p>
                </div>
            );
        }

        if (tableRows.length === 0) {
            return (
                <div className="irdai-empty-state">
                    <Search size={34} />
                    <h3>No matching report rows</h3>
                    <p>Try clearing search terms or switching to All Clients for Section 4.</p>
                </div>
            );
        }

        return (
            <div className="advanced-table-shell">
                <table className="irdai-table">
                    <thead>
                        <tr>
                            {visibleCols.map(col => (
                                <th key={col}>
                                    <button type="button" onClick={() => handleSort(col)} title={`Sort by ${col}`}>
                                        <span>{col}</span>
                                        <ChevronDown
                                            size={14}
                                            className={sortConfig.key === col && sortConfig.direction === 'desc' ? 'sort-desc' : ''}
                                        />
                                    </button>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {tableRows.map((row, rowIndex) => (
                            <tr key={`${rowIndex}-${Object.values(row)[0]}`}>
                                {visibleCols.map((col, colIndex) => {
                                    const value = row[col] !== undefined ? row[col] : 0;
                                    const isAmount = col.toLowerCase().includes('premium') || col.toLowerCase().includes('brokerage') || col.toLowerCase().includes('amount');
                                    return (
                                        <td key={col}>
                                            {colIndex === 0 ? (
                                                <span className="name-cell">{value || '-'}</span>
                                            ) : isAmount ? (
                                                <span className={Number(value) > 0 ? 'amount-pill positive' : 'amount-pill'}>{formatAmount(value)}</span>
                                            ) : (
                                                <span className={Number(value) > 0 ? 'count-badge' : ''}>{value}</span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className="irdai-reports-container">
            <div className="sidebar">
                <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            </div>

            <div className={`irdai-reports-content ${collapsed ? 'collapsed' : ''}`}>
                <div className="irdai-reports-sticky-header">
                    <Header />
                </div>
                <div className="irdai-reports-scroll-area">
                    <main className="irdai-workspace">
                        <h2>IRDIA REPORTS</h2>

                        <section className="filter-panel">
                            <div className="panel-heading">
                                <Filter size={18} />
                                <div>
                                    <h2>Filters</h2>
                                    <p title="Financial year and quarter are applied on server. Other filters refine the preview table.">
                                        Server filters and preview controls
                                    </p>
                                </div>
                            </div>

                            <div className="filter-grid">
                                <label>
                                    Form
                                    <select value={selectedForm} onChange={e => setSelectedForm(e.target.value)}>
                                        <option value="">Select Form</option>
                                        {reportCards.map(report => <option key={report.value} value={report.value}>{report.title}</option>)}
                                    </select>
                                </label>
                                <label>
                                    Financial Year
                                    <select value={selectedFY} onChange={e => setSelectedFY(e.target.value)}>
                                        <option value="">Select Financial Year</option>
                                        {financialYears.map(fy => <option key={fy} value={fy}>{fy}</option>)}
                                    </select>
                                </label>
                                <label>
                                    Quarter
                                    <select value={selectedQuarter} onChange={e => setSelectedQuarter(e.target.value)}>
                                        {quarters.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
                                    </select>
                                </label>
                                <label>
                                    Insurer
                                    <input value={filters.insurer} onChange={e => updateFilter('insurer', e.target.value)} placeholder="Search insurer rows" />
                                </label>
                                <label>
                                    Client
                                    <input value={filters.client} onChange={e => updateFilter('client', e.target.value)} placeholder="Search client rows" />
                                </label>
                                <label>
                                    Coverage Type
                                    <select value={filters.coverageType} onChange={e => updateFilter('coverageType', e.target.value)}>
                                        <option value="">All Coverage</option>
                                        <option value="Individual">Individual</option>
                                        <option value="Group">Group</option>
                                    </select>
                                </label>
                            </div>

                            <div className="toggle-row">
                                <span title="Section 4 is sorted by total premium when Top 10 is selected.">Section 4 Clients</span>
                                <div className="segmented-control" aria-label="Section 4 client list mode">
                                    <button
                                        type="button"
                                        className={section4ClientMode === 'top10' ? 'active' : ''}
                                        onClick={() => {
                                            setSection4ClientMode('top10');
                                            if (selectedForm === 'form10' && selectedFY) fetchForm10('top10');
                                        }}
                                        disabled={loading}
                                    >
                                        Top 10
                                    </button>
                                    <button
                                        type="button"
                                        className={section4ClientMode === 'all' ? 'active' : ''}
                                        onClick={() => {
                                            setSection4ClientMode('all');
                                            if (selectedForm === 'form10' && selectedFY) fetchForm10('all');
                                        }}
                                        disabled={loading}
                                    >
                                        All
                                    </button>
                                </div>
                            </div>

                            <div className="filter-actions">
                                <button type="button" className="primary-action" onClick={applyFilters} disabled={loading}>
                                    {loading ? <Loader2 size={16} className="spin" /> : <FileText size={16} />}
                                    Generate Report
                                </button>
                                <button type="button" onClick={() => exportReport('xlsx')} disabled={!hasReportData || loading || !canExport} title={canExport ? 'Download Excel' : 'Export requires report privilege'}>
                                    <Download size={16} /> Excel
                                </button>
                                <button type="button" onClick={() => exportReport('csv')} disabled={!hasReportData || loading || !canExport} title={canExport ? 'Download CSV' : 'Export requires report privilege'}>
                                    <FileText size={16} /> CSV
                                </button>
                                <button type="button" disabled title="PDF export endpoint is not available yet.">
                                    <FileSpreadsheet size={16} /> PDF
                                </button>
                                <button type="button" onClick={savePreset}>
                                    <Save size={16} /> Save Preset
                                </button>
                                <button type="button" onClick={clearFilters}>
                                    <RotateCcw size={16} /> Reset
                                </button>
                            </div>

                            <div className="mini-list">
                                <h3><Save size={15} /> Saved Presets</h3>
                                {savedPresets.length === 0 ? <p>No saved presets yet.</p> : savedPresets.map(preset => (
                                    <button key={preset.id} type="button" onClick={() => applyPreset(preset)}>
                                        {preset.name}
                                    </button>
                                ))}
                            </div>

                        </section>

                        <section className="report-card-grid">
                            {reportCards.map(card => {
                                const Icon = card.icon;
                                const isActive = selectedForm === card.value;
                                return (
                                    <article key={card.value} className={`report-card ${card.accent} ${isActive ? 'active' : ''}`}>
                                        <div className="report-card-top">
                                            <span className="report-icon"><Icon size={22} /></span>
                                            <span className="report-eyebrow">{card.eyebrow}</span>
                                        </div>
                                        <h2>{card.title}</h2>
                                        <p>{card.description}</p>
                                        <div className="report-card-actions">
                                            <button type="button" onClick={() => setSelectedForm(card.value)}>
                                                <Eye size={16} /> View
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => generateReport(card.value)}
                                            >
                                                <FileText size={16} /> Generate
                                            </button>
                                        </div>
                                    </article>
                                );
                            })}
                        </section>

                        <section className="report-surface">
                            <div className="surface-toolbar">
                                <div>
                                    <span className="active-report-badge">
                                        {activeFormCard ? activeFormCard.title : 'No report selected'}
                                    </span>
                                    <h2>{selectedForm === 'form10' ? activeSectionConfig.description : activeFormCard?.description || 'Report Preview'}</h2>
                                </div>
                                <div className="table-search">
                                    <Search size={16} />
                                    <input
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        placeholder="Search within report"
                                    />
                                </div>
                            </div>

                            <div className="summary-strip">
                                <div>
                                    <Users size={18} />
                                    <span>Rows</span>
                                    <strong>{summary.rows}</strong>
                                </div>
                                <div>
                                    <FileText size={18} />
                                    <span>Policies</span>
                                    <strong>{formatAmount(summary.policies)}</strong>
                                </div>
                                <div>
                                    <BadgeIndianRupee size={18} />
                                    <span>Premium</span>
                                    <strong>{formatAmount(summary.premium)}</strong>
                                </div>
                                <div>
                                    <CalendarDays size={18} />
                                    <span>FY / Quarter</span>
                                    <strong>{selectedFY || 'All'} / {selectedQuarter}</strong>
                                </div>
                            </div>

                            {selectedForm === 'form10' && (
                                <nav className="section-tabs" aria-label="Form 10 sections">
                                    {form10Sections.map(section => (
                                        <button
                                            key={section.key}
                                            type="button"
                                            className={activeSection === section.key ? 'active' : ''}
                                            onClick={() => {
                                                setActiveSection(section.key);
                                                setSortConfig({ key: '', direction: 'asc' });
                                            }}
                                        >
                                            {section.label}
                                            <span>{(form10Data[section.key] || []).length}</span>
                                        </button>
                                    ))}
                                </nav>
                            )}

                            <div className="report-hint">
                                <Info size={15} />
                                <span>Use column headers to sort. Search and preview filters refine currently loaded rows instantly.</span>
                            </div>

                            {renderTable()}
                        </section>

                        <section className="export-history-panel">
                            <div className="surface-toolbar">
                                <div>
                                    <span className="active-report-badge">Audit Log</span>
                                    <h2>Export History</h2>
                                </div>
                                <div className="table-search">
                                    <Search size={16} />
                                    <input
                                        value={historySearch}
                                        onChange={e => setHistorySearch(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                setHistoryPage(1);
                                                fetchExportHistory(1, historySearch);
                                            }
                                        }}
                                        placeholder="Search export history"
                                    />
                                </div>
                            </div>

                            <div className="advanced-table-shell export-history-table">
                                <table className="irdai-table">
                                    <thead>
                                        <tr>
                                            <th>Report Name</th>
                                            <th>Form Name</th>
                                            <th>Report Type</th>
                                            <th>Exported By</th>
                                            <th>Export Date & Time</th>
                                            <th>File Name</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {exportHistory.length === 0 ? (
                                            <tr>
                                                <td colSpan={6}>No export history found</td>
                                            </tr>
                                        ) : exportHistory.map(item => (
                                            <tr key={item._id}>
                                                <td><span className="name-cell">{item.reportName}</span></td>
                                                <td>{item.formName}</td>
                                                <td><span className="count-badge">{item.reportType}</span></td>
                                                <td>{item.exportedByName}</td>
                                                <td>{item.exportedAt ? new Date(item.exportedAt).toLocaleString() : '-'}</td>
                                                <td>{item.fileName}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="history-pagination">
                                <span>
                                    Page {historyPagination.page} of {historyPagination.totalPages} ({historyPagination.total} records)
                                </span>
                                <div>
                                    <button
                                        type="button"
                                        onClick={() => setHistoryPage(page => Math.max(page - 1, 1))}
                                        disabled={historyPagination.page <= 1}
                                    >
                                        Previous
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setHistoryPage(page => Math.min(page + 1, historyPagination.totalPages))}
                                        disabled={historyPagination.page >= historyPagination.totalPages}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </section>
                    </main>
                </div>
            </div>
        </div>
    );
};

export default IrdaiReports;
